import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteSeo, ogImage, siteUrl } from "@/lib/seo";

/** Create-or-update a <meta> tag by attribute selector. */
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const STRUCTURED_DATA_ID = "route-structured-data";

interface Props {
  /** Optional per-page JSON-LD (Service, Article, Place…). */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Keeps <title>, meta description, canonical, and social tags in sync with the
 * current route. Static per-route HTML is also emitted at build time by
 * scripts/generate-static-seo.mjs so non-JS crawlers get the same values.
 */
const Seo = ({ structuredData }: Props) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const canonical = `${siteUrl}${seo.path === "/" ? "/" : seo.path}`;

    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setLink("canonical", canonical);

    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", ogImage);
  }, [pathname]);

  useEffect(() => {
    const existing = document.getElementById(STRUCTURED_DATA_ID);
    if (existing) existing.remove();
    if (!structuredData) return;

    const script = document.createElement("script");
    script.id = STRUCTURED_DATA_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [structuredData]);

  return null;
};

export default Seo;
